<script>
  import { Link } from '@inertiajs/svelte'
  import ApplicationLayout from '../../components/ApplicationLayout/index.svelte'
  import StatusBadge from '../../components/StatusBadge.svelte'
  import QuantityCard from '../../components/QuantityCard/index.svelte'
  import StoredInSpine from '../../components/StoredInSpine/index.svelte'
  import MoveItemModal from '../../components/MoveItemModal/index.svelte'

  let { item } = $props()

  let moveOpen = $state(false)

  // Gallery: large preview swaps when a thumbnail is clicked. Defaults to cover.
  const images = $derived(item.images || [])
  let activeImageUrl = $state(
    (item.images && item.images.length ? item.images[0].url : null) || item.image_url || null
  )

  // Stored-in chain: array of { kind, label, href, direct }, outermost first.
  const storedIn = $derived(item.stored_in || [])
  const warranty = $derived(item.warranty_status || { state: 'none' })

  function formatCategory(value) {
    if (!value) return null
    return value.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
  }

  function formatCurrency(amount) {
    if (amount === null || amount === undefined || amount === '') return null
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  function formatDate(date) {
    if (!date) return null
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }

  // Subline: brand · model joined, skipping blanks.
  const subline = $derived([item.brand, item.model_number].filter(Boolean).join(' · '))

  const hasFinancial = $derived(
    !!(item.purchase_price || item.current_value || item.purchase_date || warranty.state !== 'none')
  )
  const hasSpecs = $derived(
    !!(item.brand || item.model_number || item.serial_number || item.color || item.dimensions || item.weight)
  )

</script>

<svelte:head>
  <title>{item.name} | Maset</title>
</svelte:head>

<ApplicationLayout>
  <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

    <!-- Breadcrumb -->
    <nav class="flex items-center gap-2 flex-wrap text-sm text-gray-500 mb-5">
      <Link href="/dashboard" class="font-semibold text-brand-700 hover:underline">Maset</Link>
      {#each storedIn as level}
        <span class="text-gray-300">/</span>
        {#if level.href}
          <Link href={level.href} class="font-semibold text-brand-700 hover:underline">{level.label}</Link>
        {:else}
          <span>{level.label}</span>
        {/if}
      {/each}
      <span class="text-gray-300">/</span>
      <span class="text-gray-900 font-semibold">{item.name}</span>
    </nav>

    <!-- Top: gallery + summary -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

      <!-- Gallery -->
      <section class="bg-white rounded-2xl border border-gray-200 shadow-sm p-3.5">
        {#if activeImageUrl}
          <div class="relative aspect-[4/3] rounded-xl overflow-hidden bg-gray-100">
            <img src={activeImageUrl} alt={item.name} class="w-full h-full object-cover" />
            {#if images.length > 1}
              <span class="absolute top-3 right-3 bg-black/55 text-white text-xs font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm">
                {images.length} photos
              </span>
            {/if}
          </div>
          {#if images.length > 1}
            <div class="flex gap-2 mt-2.5 flex-wrap">
              {#each images as image (image.id)}
                <button
                  type="button"
                  onclick={() => (activeImageUrl = image.url)}
                  class="w-16 h-16 rounded-lg overflow-hidden border-2 transition {activeImageUrl === image.url ? 'border-brand-600' : 'border-transparent hover:border-gray-300'}"
                >
                  <img src={image.thumbnail_url} alt={item.name} class="w-full h-full object-cover" />
                </button>
              {/each}
            </div>
          {/if}
        {:else}
          <div class="aspect-[4/3] rounded-xl bg-brand-50 text-brand-700 grid place-items-center text-5xl font-bold">
            {(item.name || '?').charAt(0).toUpperCase()}
          </div>
        {/if}
      </section>

      <!-- Summary -->
      <div class="flex flex-col gap-4">
        <div class="relative overflow-hidden rounded-2xl px-6 py-5 text-white shadow-md bg-gradient-to-br from-brand-900 via-brand-700 to-teal-600">
          <div class="absolute inset-0 pointer-events-none" style="background: radial-gradient(420px 140px at 95% -30%, rgba(255,255,255,.16), transparent 60%);"></div>
          <div class="relative">
            <p class="text-xs uppercase tracking-wider font-bold text-brand-100">
              {formatCategory(item.category)}{#if item.subcategory} · {formatCategory(item.subcategory)}{/if}
            </p>
            <h1 class="text-2xl sm:text-3xl font-extrabold tracking-tight mt-1.5">{item.name}</h1>
            {#if subline}
              <p class="text-white/80 text-sm mt-1.5">{subline}</p>
            {/if}
            <div class="flex flex-wrap gap-2 mt-4">
              <StatusBadge value={item.status} />
              <StatusBadge value={item.condition} />
              {#if item.is_valuable}
                <span class="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-white/15 text-white">
                  <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h12l3 6-9 12L3 9l3-6z"/><path d="M3 9h18"/><path d="M9 3l3 6 3-6"/><path d="M12 9v12"/></svg>
                  Valuable
                </span>
              {/if}
              {#if item.is_favorite}
                <span class="inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-2.5 py-1 bg-white/15 text-white">
                  <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 6c-2.5 4.5-9.5 9-9.5 9z"/></svg>
                  Favorite
                </span>
              {/if}
            </div>
          </div>
        </div>

        <!-- Stat band -->
        <div class="grid grid-cols-2 gap-3">
          <QuantityCard {item} />
          <div class="bg-white border border-gray-200 rounded-xl px-4 py-3.5 shadow-sm">
            <div class="text-[11px] uppercase tracking-wide font-bold text-gray-500">Current value</div>
            <div class="text-xl font-extrabold text-gray-900 mt-1">{formatCurrency(item.current_value) || '—'}</div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex gap-2.5">
          <Link href={`/properties/${item.property.id}/items/${item.id}/edit`} class="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand-700 text-white hover:bg-brand-800">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
            Edit Item
          </Link>
          <button type="button" onclick={() => (moveOpen = true)} class="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 17h9a4 4 0 004-4V7"/><path d="M14 7l3-3 3 3"/></svg>
            Move
          </button>
        </div>
      </div>
    </div>

    <!-- Detail sections -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-6">

      <!-- Description -->
      {#if item.description || item.notes}
        <section class="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
            <span class="w-7 h-7 rounded-lg grid place-items-center bg-brand-50 text-brand-700">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16"/><path d="M4 10h16"/><path d="M4 15h10"/><path d="M4 20h7"/></svg>
            </span>
            <h2 class="text-sm font-bold text-gray-900">Description &amp; Notes</h2>
          </div>
          <div class="p-5 space-y-3">
            {#if item.description}
              <p class="text-sm text-gray-700 whitespace-pre-line">{item.description}</p>
            {/if}
            {#if item.notes}
              <p class="text-sm text-gray-500 whitespace-pre-line border-t border-gray-100 pt-3">{item.notes}</p>
            {/if}
          </div>
        </section>
      {/if}

      <!-- Stored in -->
      <section class="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
          <span class="w-7 h-7 rounded-lg grid place-items-center bg-brand-50 text-brand-700">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          </span>
          <h2 class="text-sm font-bold text-gray-900">Stored in</h2>
        </div>
        <div class="p-5">
          <StoredInSpine chain={storedIn} />
        </div>
      </section>

      <!-- Specifications -->
      {#if hasSpecs}
        <section class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
            <span class="w-7 h-7 rounded-lg grid place-items-center bg-brand-50 text-brand-700">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21l6-6"/><path d="M14.5 3.5l6 6L9 21H3v-6L14.5 3.5z"/><path d="M12 6l6 6"/></svg>
            </span>
            <h2 class="text-sm font-bold text-gray-900">Specifications</h2>
          </div>
          <div class="p-5 grid grid-cols-2 gap-x-5 gap-y-4">
            {#if item.brand}<div><div class="field-k">Brand</div><div class="field-v">{item.brand}</div></div>{/if}
            {#if item.model_number}<div><div class="field-k">Model</div><div class="field-v">{item.model_number}</div></div>{/if}
            {#if item.serial_number}<div><div class="field-k">Serial number</div><div class="field-v">{item.serial_number}</div></div>{/if}
            {#if item.color}<div><div class="field-k">Color</div><div class="field-v">{item.color}</div></div>{/if}
            {#if item.dimensions}<div><div class="field-k">Dimensions</div><div class="field-v">{item.dimensions}</div></div>{/if}
            {#if item.weight}<div><div class="field-k">Weight</div><div class="field-v">{item.weight} kg</div></div>{/if}
          </div>
        </section>
      {/if}

      <!-- Financial -->
      {#if hasFinancial}
        <section class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
            <span class="w-7 h-7 rounded-lg grid place-items-center bg-brand-50 text-brand-700">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M17 6.5H9.5a3 3 0 000 6h5a3 3 0 010 6H6"/></svg>
            </span>
            <h2 class="text-sm font-bold text-gray-900">Financial</h2>
          </div>
          <div class="p-5">
            <div class="grid grid-cols-2 gap-x-5 gap-y-4">
              {#if item.purchase_price}<div><div class="field-k">Purchase price</div><div class="field-v">{formatCurrency(item.purchase_price)}</div></div>{/if}
              {#if item.current_value}<div><div class="field-k">Current value</div><div class="field-v">{formatCurrency(item.current_value)}</div></div>{/if}
              {#if item.purchase_date}<div><div class="field-k">Purchase date</div><div class="field-v">{formatDate(item.purchase_date)}</div></div>{/if}
            </div>
            {#if warranty.state !== 'none'}
              <div class="mt-4 flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-sm font-semibold {warranty.state === 'expired' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-brand-50 border border-brand-200 text-brand-800'}">
                <svg class="w-[18px] h-[18px] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>
                {#if warranty.state === 'expired'}
                  <span>Warranty expired · {formatDate(warranty.expires_on)} ({Math.abs(warranty.days_left)} days ago)</span>
                {:else}
                  <span>Warranty active · expires {formatDate(warranty.expires_on)} ({warranty.days_left} days left)</span>
                {/if}
              </div>
            {/if}
          </div>
        </section>
      {/if}

      <!-- Record -->
      <section class="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
          <span class="w-7 h-7 rounded-lg grid place-items-center bg-brand-50 text-brand-700">
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="11" x2="12" y2="16"/><circle cx="12" cy="8" r="0.5" fill="currentColor"/></svg>
          </span>
          <h2 class="text-sm font-bold text-gray-900">Record</h2>
        </div>
        <div class="px-5 py-2">
          {#if item.created_at}
            <div class="flex justify-between gap-3 py-2.5 border-b border-dashed border-gray-100 text-[13px]">
              <span class="text-gray-500">Created</span><span class="text-gray-800 font-semibold">{formatDate(item.created_at)}</span>
            </div>
          {/if}
          {#if item.updated_at}
            <div class="flex justify-between gap-3 py-2.5 border-b border-dashed border-gray-100 text-[13px]">
              <span class="text-gray-500">Last updated</span><span class="text-gray-800 font-semibold">{formatDate(item.updated_at)}</span>
            </div>
          {/if}
          <div class="flex justify-between gap-3 py-2.5 text-[13px]">
            <span class="text-gray-500">Item ID</span><span class="text-gray-400 font-mono text-[11px]">{item.id}</span>
          </div>
        </div>
      </section>
    </div>
  </div>

  <MoveItemModal open={moveOpen} {item} onClose={() => (moveOpen = false)} />
</ApplicationLayout>

<style>
  .field-k { font-size: 11px; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; color: #6b7280; }
  .field-v { margin-top: 3px; font-size: 14px; color: #111827; }
</style>
